import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { PowerIrradianceData } from '../../services/technicalApi';

interface PowerIrradianceChartProps {
  data: PowerIrradianceData[];
  correlation: number;
  loading?: boolean;
}

const PowerIrradianceChart: React.FC<PowerIrradianceChartProps> = ({ 
  data, 
  correlation, 
  loading = false 
}) => {
  if (loading) {
    return (
      <div className="h-80 flex items-center justify-center">
        <div className="animate-pulse bg-gray-200 dark:bg-gray-700 w-full h-full rounded-lg"></div>
      </div>
    );
  }

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {formatTime(label)}
          </p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value.toFixed(2)} {entry.name.includes('Power') ? 'kW' : 'W/m²'}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-4">
      {/* Correlation Badge */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Live Power vs Irradiance
        </h3>
        <div className={`px-3 py-1 rounded-full text-sm font-medium ${
          correlation > 0.8 
            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
            : correlation > 0.6
            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
        }`}>
          Correlation: {(correlation * 100).toFixed(1)}%
        </div>
      </div>

      {/* Chart */}
      <div className="h-72 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 40, left: 10, bottom: 10 }}>
            <CartesianGrid strokeDasharray="2 2" className="opacity-30" stroke="currentColor" />
            <XAxis 
              dataKey="timestamp" 
              tickFormatter={formatTime}
              tick={{ fontSize: 11, fill: 'currentColor' }}
              className="text-gray-600 dark:text-gray-400"
              axisLine={false}
              tickLine={false}
            />
            <YAxis 
              yAxisId="power"
              orientation="left"
              tick={{ fontSize: 11, fill: 'currentColor' }}
              className="text-gray-600 dark:text-gray-400"
              axisLine={false}
              tickLine={false}
              label={{ 
                value: 'Power (kW)', 
                angle: -90, 
                position: 'insideLeft',
                style: { textAnchor: 'middle' }
              }}
            />
            <YAxis 
              yAxisId="irradiance"
              orientation="right"
              tick={{ fontSize: 11, fill: 'currentColor' }}
              className="text-gray-600 dark:text-gray-400"
              axisLine={false}
              tickLine={false}
              label={{ 
                value: 'Irradiance (W/m²)', 
                angle: 90, 
                position: 'insideRight',
                style: { textAnchor: 'middle' }
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: '10px' }}
              iconType="line"
            />
            <Line
              yAxisId="power"
              type="monotone"
              dataKey="power_kw"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ fill: '#3b82f6', strokeWidth: 1, r: 2 }}
              name="Power"
              animationDuration={600}
            />
            <Line
              yAxisId="irradiance"
              type="monotone"
              dataKey="irradiance_wm2"
              stroke="#f59e0b"
              strokeWidth={2}
              dot={{ fill: '#f59e0b', strokeWidth: 1, r: 2 }}
              name="Irradiance"
              animationDuration={600}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PowerIrradianceChart;